<script setup>
  import { nextTick, onMounted, ref, watch } from "vue";
  import { ArrowUp, Square, Paperclip, X } from "lucide-vue-next";
  import VoiceInputButton from "./VoiceInputButton.vue";

  const props = defineProps({
    isStreaming: { type: Boolean, default: false },
    allowFiles: { type: Boolean, default: false },
    acceptedFiles: { type: String, default: ".pdf,.docx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp,.gif" },
    maxFiles: { type: Number, default: 3 },
    autofocus: { type: Boolean, default: false },
  });

  const emit = defineEmits(["send", "stop"]);

  const inputMessage = ref("");
  const attachedFiles = ref([]);
  const fileInput = ref(null);

  // The words Gladia has not settled yet. They live here rather than in
  // inputMessage so a guess can never be sent as if it had been typed.
  const hypothesis = ref("");
  const isDictating = ref(false);

  const voiceButton = ref(null);
  const textarea = ref(null);
  const preview = ref(null);

  // The preview stands in for the textarea while dictating, so both have to sit
  // in exactly the same box: one list of classes, not two that drift apart.
  const FIELD_CLASSES = "w-full rounded-xl bg-transparent px-3.5 pt-3 pb-10 text-13 min-h-[88px] max-h-32";

  function handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  }

  // Sending mid-sentence keeps what was spoken, like taking the field over does:
  // cancelDictation commits the hypothesis into inputMessage before it is read.
  function submit() {
    voiceButton.value?.cancelDictation();

    const message = inputMessage.value.trim();
    if (!message) return;

    emit(
      "send",
      message,
      attachedFiles.value.map((file) => file.raw),
    );
    inputMessage.value = "";
    clearFiles();
  }

  function onFilesSelected(event) {
    const selected = Array.from(event.target.files || []);
    const room = props.maxFiles - attachedFiles.value.length;
    attachedFiles.value.push(...selected.slice(0, room).map((file) => ({ name: file.name, raw: file })));
    if (fileInput.value) fileInput.value.value = "";
  }

  function removeFile(index) {
    attachedFiles.value.splice(index, 1);
  }

  function clearFiles() {
    attachedFiles.value = [];
    if (fileInput.value) fileInput.value.value = "";
  }

  function fillInput(text) {
    inputMessage.value = text;
  }

  function focus() {
    textarea.value?.focus({ preventScroll: true });
  }

  onMounted(() => {
    if (props.autofocus) focus();
  });

  watch(
    () => props.isStreaming,
    async (streaming) => {
      if (streaming || !props.autofocus) return;
      await nextTick();
      const active = document.activeElement;
      if (!active || active === document.body) focus();
    },
  );

  async function takeOverField() {
    voiceButton.value?.cancelDictation();
    await nextTick();
    textarea.value?.focus();
  }

  // New words land at the bottom of the box, which only scrolls itself while the
  // caret is in it — and during dictation there is no caret.
  watch([inputMessage, hypothesis], async () => {
    if (!isDictating.value) return;

    await nextTick();
    if (preview.value) preview.value.scrollTop = preview.value.scrollHeight;
  });

  defineExpose({ fillInput, focus });
</script>

<template>
  <div class="px-3 pb-3 pt-1 flex-shrink-0">
    <div class="relative rounded-lg border border-border bg-background shadow-xs transition-colors focus-within:border-ring">
      <!-- Attached files: a sibling of the field, so the chips stay visible
           whether the field is the textarea or the dictation preview. -->
      <div
        v-if="attachedFiles.length"
        class="flex flex-wrap gap-1.5 px-3.5 pt-3">
        <span
          v-for="(file, index) in attachedFiles"
          :key="index"
          class="inline-flex items-center gap-1.5 rounded-md bg-background px-2 py-1 text-2xs text-foreground/80 border border-border">
          <Paperclip class="h-3 w-3 shrink-0" />
          <span class="max-w-[180px] truncate">{{ file.name }}</span>
          <button
            class="text-muted-foreground hover:text-destructive"
            title="Remove file"
            @click="removeFile(index)">
            <X class="h-3 w-3" />
          </button>
        </span>
      </div>

      <div
        v-if="isDictating"
        ref="preview"
        :class="[FIELD_CLASSES, 'cursor-text overflow-y-auto whitespace-pre-wrap break-words']"
        @click="takeOverField">
        <span>{{ inputMessage }}</span>
        <span
          v-if="hypothesis"
          class="text-muted-foreground">{{ inputMessage ? " " : "" }}{{ hypothesis }}</span>
        <span
          v-if="!inputMessage && !hypothesis"
          class="text-muted-foreground/50">Listening…</span>
      </div>
      <textarea
        v-else
        ref="textarea"
        v-model="inputMessage"
        :disabled="isStreaming"
        placeholder="Ask Episto AI"
        rows="3"
        :class="[FIELD_CLASSES, 'resize-none placeholder:text-muted-foreground/50 focus:outline-none']"
        @keydown="handleKeydown" />

      <div
        v-if="allowFiles"
        class="absolute bottom-2 left-2">
        <input
          ref="fileInput"
          type="file"
          multiple
          :accept="acceptedFiles"
          class="hidden"
          @change="onFilesSelected" />
        <button
          :disabled="isStreaming || attachedFiles.length >= maxFiles"
          class="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground transition-colors hover:bg-muted-foreground/10 disabled:opacity-40"
          :title="attachedFiles.length >= maxFiles ? `${maxFiles} files maximum` : 'Attach a file (brief, screenshot…)'"
          @click="fileInput?.click()">
          <Paperclip class="w-4 h-4" />
        </button>
      </div>

      <div class="absolute bottom-2 right-2 flex items-center gap-1">
        <VoiceInputButton
          ref="voiceButton"
          v-model="inputMessage"
          v-model:recording="isDictating"
          v-model:hypothesis="hypothesis"
          :disabled="isStreaming"
          live />
        <!-- Stop button during streaming -->
        <button
          v-if="isStreaming"
          class="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center transition-colors hover:bg-foreground/80"
          @click="emit('stop')">
          <Square class="w-3 h-3 fill-current" />
        </button>
        <!-- Send button -->
        <button
          v-else
          :disabled="!inputMessage.trim() && !hypothesis.trim()"
          class="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
          :class="
            inputMessage.trim() || hypothesis.trim()
              ? 'bg-foreground text-background hover:bg-foreground/80'
              : 'bg-muted-foreground/20 text-muted-foreground cursor-not-allowed'
          "
          @click="submit()">
          <ArrowUp
            class="w-4 h-4"
            stroke-width="2.5" />
        </button>
      </div>
    </div>
  </div>
</template>
